/**
 * @format
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { mobileNavMediaQuery } from '../../../layout/layoutMetrics';
import { useParams } from 'react-router-dom';
import { useClient } from '../../../app/context/ClientContext';
import dayjs from 'dayjs';
import { useAuth } from '../../../app/context/AuthContext';
import { MODULE_LIST } from '../../../constants/commons';
import { dispatchEditProject } from '../../../layout/workroomEditEvents';

const STATUS_OPTIONS = ['Active'];

const DATE_COL_DEFAULT_PX = 36;
const DATE_COL_EXPANDED_PX = 56;
const PANEL_WIDTH_DEFAULT = 190;
const PANEL_WIDTH_EXPANDED = 230;
const DATE_MONTH_WIDTH_PX = 22;

function dateLabelWraps(el) {
  if (!el) return false;
  if (el.scrollWidth > el.clientWidth + 1) return true;

  const range = document.createRange();
  range.selectNodeContents(el);
  if (range.getClientRects().length > 1) return true;

  return el.offsetHeight > 16;
}

export default function WorkroomStatusPanel({ onExpandedLayoutChange } = {}) {
  const [status, setStatus] = useState('Active');
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [projectSteps, setProjectSteps] = useState([]);
  const [expandForWrap, setExpandForWrap] = useState(false);
  const dateLabelRefs = useRef([]);

  const { projectId: routeProjectId } = useParams();
  const { selectedProject } = useClient();

  const activeStepIndex = projectSteps.findIndex((step) => step.active);

  const { userPermissions } = useAuth();
  const timelineModule = Object.hasOwn(
    userPermissions,
    MODULE_LIST.PROJECT_TIMELINE
  )
    ? userPermissions[MODULE_LIST.PROJECT_TIMELINE]
    : false;

  let canEditTimeline = false;
  let canViewTimeline = false;

  if (timelineModule && timelineModule.view) {
    canViewTimeline = true;
    canEditTimeline = timelineModule.edit;
  }

  useEffect(() => {
    if (!selectedProject?.projectStatus) {
      setProjectSteps([]);
      setExpandForWrap(false);
      onExpandedLayoutChange?.(false);
      return;
    }

    const { projectStatus } = selectedProject;
    const now = dayjs();

    const rawItems = Object.entries(projectStatus).map(([key, val]) => ({
      id: key,
      label: key,
      date: dayjs(val),
      dateLabel: dayjs(val).format('DD MMM'),
    }));

    // Sort chronologically
    rawItems.sort((a, b) => a.date.valueOf() - b.date.valueOf());

    // Active = the nearest upcoming (soonest future) date.
    // Everything before it is "past", everything after it is "future/upcoming".
    const activeIndex = rawItems.findIndex((item) => item.date.isAfter(now));

    const items = rawItems.map((item, idx) => ({
      id: item.id,
      label: item.label,
      dateLabel: item.dateLabel,
      active: idx === activeIndex,
    }));

    // Reset to default layout; layout effect re-expands only if labels wrap.
    setExpandForWrap(false);
    onExpandedLayoutChange?.(false);
    setProjectSteps(items);
    dateLabelRefs.current = [];
  }, [selectedProject, onExpandedLayoutChange]);

  useLayoutEffect(() => {
    if (expandForWrap || !projectSteps.length) return;

    const needsExpand = dateLabelRefs.current.some(dateLabelWraps);
    if (!needsExpand) return;

    setExpandForWrap(true);
    onExpandedLayoutChange?.(true);
  }, [projectSteps, expandForWrap, onExpandedLayoutChange]);

  const panelWidth = expandForWrap ? PANEL_WIDTH_EXPANDED : PANEL_WIDTH_DEFAULT;
  const dateColPx = expandForWrap ? DATE_COL_EXPANDED_PX : DATE_COL_DEFAULT_PX;

  return (
    <Box
      sx={{
        width: panelWidth,
        flexShrink: 0,
        alignSelf: 'flex-start',
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        pt: 1,
        pb: 2,
        [`@media ${mobileNavMediaQuery}`]: {
          width: '100%',
          position: 'static',
          order: 0,
          pt: 0,
          pb: 0,
          gap: 1.75,
          borderRadius: '16px',
          background:
            'linear-gradient(135deg, rgba(255, 255, 255, 0.07), rgba(255, 255, 255, 0.03))',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          px: 2,
          py: 2,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          [`@media ${mobileNavMediaQuery}`]: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          },
        }}
      >
        <Box
          component="button"
          type="button"
          aria-haspopup="listbox"
          aria-expanded={Boolean(statusAnchor)}
          onClick={(e) => setStatusAnchor(e.currentTarget)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            p: 0,
            m: 0,
            ml: 1.5,
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            color: 'inherit',
            '&:hover .status-value': { opacity: 0.85 },
          }}
        >
          <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            Status:{' '}
            <Box
              component="span"
              className="status-value"
              sx={{
                color:
                  status === 'Active' ? '#5EEAD4' : 'rgba(255,255,255,0.85)',
                fontWeight: 600,
              }}
            >
              {status}
            </Box>
          </Typography>
          <KeyboardArrowDownIcon
            sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)' }}
          />
        </Box>

        <Menu
          anchorEl={statusAnchor}
          open={Boolean(statusAnchor)}
          onClose={() => setStatusAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 0.75,
              minWidth: 150,
              bgcolor: 'rgba(28, 32, 30, 0.96)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
            },
          }}
        >
          {STATUS_OPTIONS.map((option) => (
            <MenuItem
              key={option}
              selected={option === status}
              onClick={() => {
                setStatus(option);
                setStatusAnchor(null);
              }}
              sx={{
                fontSize: 13,
                color: 'rgba(255,255,255,0.85)',
                '&.Mui-selected': {
                  bgcolor: 'rgba(255,255,255,0.08)',
                },
                '&.Mui-selected:hover': {
                  bgcolor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              <ListItemText primary={option} />
              {option === status ? (
                <ListItemIcon sx={{ minWidth: 28, color: '#5EEAD4' }}>
                  <CheckIcon sx={{ fontSize: 16 }} />
                </ListItemIcon>
              ) : null}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: canViewTimeline ? 'block' : 'none',
          [`@media ${mobileNavMediaQuery}`]: {
            display: canViewTimeline ? 'flex' : 'none',
            overflowX: 'auto',
            gap: 0,
            pb: 0.5,
            mx: -0.5,
            px: 0.5,
            WebkitOverflowScrolling: 'touch',
            '&::-webkit-scrollbar': { height: '4px' },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 10,
            },
          },
        }}
      >
        {projectSteps.map((step, index) => {
          const isActive = Boolean(step.active);
          const isPast = activeStepIndex >= 0 && index < activeStepIndex;
          const isLast = index === projectSteps.length - 1;
          const [dayPart = '0/0', monthPart = ''] = (
            step.dateLabel || '0/0'
          ).split(' ');

          return (
            <Box
              key={step.id}
              sx={{
                display: 'grid',
                gridTemplateColumns: `${dateColPx}px 12px 1fr`,
                alignItems: 'center',
                columnGap: 1,
                minHeight: 34,
                position: 'relative',
                [`@media ${mobileNavMediaQuery}`]: {
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  flex: '1 0 auto',
                  minWidth: 72,
                  minHeight: 0,
                  pt: 0.5,
                  pb: 0.25,
                },
              }}
            >
              <Box
                component="span"
                ref={(el) => {
                  dateLabelRefs.current[index] = el;
                }}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'baseline',
                  gap: '3px',
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.85)',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  visibility: step.dateLabel ? 'visible' : 'hidden',
                  [`@media ${mobileNavMediaQuery}`]: {
                    order: 0,
                    justifyContent: 'center',
                    mb: 0.75,
                    minHeight: 12,
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}
                >
                  {dayPart}
                </Box>
                <Box
                  component="span"
                  sx={{
                    fontSize: 'inherit',
                    fontWeight: 'inherit',
                    width: DATE_MONTH_WIDTH_PX,
                    flexShrink: 0,
                    textAlign: 'left',
                  }}
                >
                  {monthPart}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  alignSelf: 'stretch',
                  zIndex: 1,
                  [`@media ${mobileNavMediaQuery}`]: {
                    order: 1,
                    alignSelf: 'center',
                    width: '100%',
                    height: 16,
                  },
                }}
              >
                {!isLast ? (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      bottom: -17,
                      width: '1px',
                      transform: 'translateX(-50%)',
                      bgcolor: 'rgba(255,255,255,0.18)',
                      [`@media ${mobileNavMediaQuery}`]: {
                        left: '50%',
                        right: '-50%',
                        top: '50%',
                        bottom: 'auto',
                        width: 'auto',
                        height: '1px',
                        transform: 'translateY(-50%)',
                      },
                    }}
                  />
                ) : null}
                <Box
                  aria-hidden
                  sx={{
                    width: isActive ? 10 : 7,
                    height: isActive ? 10 : 7,
                    borderRadius: '50%',
                    bgcolor: isActive
                      ? '#F5C542'
                      : isPast
                        ? 'rgba(255,255,255,0.7)'
                        : 'rgba(255,255,255,0.35)',
                    boxShadow: isActive
                      ? '0 0 10px rgba(245, 197, 66, 0.65)'
                      : 'none',
                    position: 'relative',
                    zIndex: 1,
                  }}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive
                    ? 'rgba(255,255,255,0.95)'
                    : 'rgba(255,255,255,0.55)',
                  lineHeight: 1.2,
                  [`@media ${mobileNavMediaQuery}`]: {
                    order: 2,
                    fontSize: 11,
                    textAlign: 'center',
                    mt: 0.75,
                    px: 0.25,
                  },
                }}
              >
                {step.label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={() => {
          if (!canEditTimeline) return;
          const projectId =
            selectedProject?.projectId ??
            selectedProject?.Id ??
            selectedProject?.id ??
            routeProjectId;
          dispatchEditProject({ projectId });
        }}
        sx={{
          display: canEditTimeline ? 'flex' : 'none',
          alignItems: 'center',
          gap: 0.6,
          p: 0,
          m: 0,
          ml: 1.5,
          border: 0,
          background: 'transparent',
          color: 'rgba(255,255,255,0.42)',
          cursor: 'pointer',
          width: 'fit-content',
          '&:hover': { color: 'rgba(255,255,255,0.65)' },
        }}
      >
        <EditNoteOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 12 }}>edit timeline</Typography>
      </Box>
    </Box>
  );
}
